import React, { useEffect, useState } from 'react';
import {
    Breadcrumb, Layout, Row, Col, Form, Button, Select,
    Table, Card, Space, Input, DatePicker,
    Spin, Modal, Switch, InputNumber, Tooltip, message,
    Tag // --- 1. Imported Tag ---
} from 'antd';
import { Link } from "react-router-dom";
import {
    PlusOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { APPBACKGROUND_COLOR, MAINAPIURL } from '../appsettings';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

const OFFER_TYPES = [
    { value: 'FOV', label: 'First Order Value Based' },
    { value: 'COV', label: 'Common Offer Value Based' },
    { value: 'LMP', label: 'Last Month Point Based' },
    { value: 'PTMP', label: 'Previous Three Months Point Based' },
    { value: 'LMR', label: 'Last Month Recruit Based' },
    { value: 'WPC1', label: 'Welcome Program Consultant Last Month' },
    { value: 'WPC2', label: 'Welcome Program Consultant Last Two Months' },
    { value: 'WPC3', label: 'Welcome Program Consultant Last Three Months' },
    { value: 'WPS1', label: 'Welcome Program Sponsor Last Month' },
    { value: 'WPS2', label: 'Welcome Program Sponsor Last Two Months' },
    { value: 'WPS3', label: 'Welcome Program Sponsor Last Three Months' },
];

function ProductOffer() {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const USERID = localStorage.getItem("USERID");

    const [filterForm] = Form.useForm();
    const [modalForm] = Form.useForm();
    const selectedOfferCode = Form.useWatch('offerCode', modalForm);
    const isM1Applicable = ['LMP', 'PTMP', 'WPC1', 'WPC2', 'WPC3', 'WPS1', 'WPS2', 'WPS3'].includes(selectedOfferCode);
    const isM2Applicable = ['PTMP', 'WPC2', 'WPC3', 'WPS2', 'WPS3'].includes(selectedOfferCode);
    const isM3Applicable = ['PTMP', 'WPC3', 'WPS3'].includes(selectedOfferCode);
    const [isLoading, setIsLoading] = useState(false);
    const [offers, setOffers] = useState([]);
    const [products, setProducts] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);
    const [modalType, setModalType] = useState('create'); // 'create' or 'edit'
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // --- Fetch Offers ---
    const fetchOffers = async (filters = {}, page = pagination.current, pageSize = pagination.pageSize) => {
        setIsLoading(true);
        try {
            const payload = {
                offerType: filters.offerType || "",
                startDate: filters.startDate || "",
                endDate: filters.endDate || "",
                currentStatus: filters.currentStatus ?? "", // This correctly handles "true", "false", or ""
                pageLength: pageSize,
                pageNumber: page,
            };

            const res = await axios.post(`${ApiURL}/offers/offerslistfilter`, payload, {
                headers: {
                    SystemAccessToken: TOKEN,
                    'Content-Type': 'application/json',
                },
            });

            const { dataList = [], rowcount = 0 } = res.data;
            setOffers(dataList);
            setPagination({
                ...pagination,
                total: rowcount,
                current: page,
                pageSize,
            });
        } catch (error) {
            console.error("Failed to fetch offers", error);
            message.error("Failed to fetch offers");
        } finally {
            setIsLoading(false);
        }
    };

    // --- Fetch Products for dropdown ---
    const fetchProducts = async () => {
        try {
            const res = await axios.get(`${ApiURL}/offers/relatedproductsfordropdown/ALL`, {
                headers: { SystemAccessToken: TOKEN },
            });
            setProducts(res.data || []);
        } catch (error) {
            console.error("Failed to fetch products", error);
        }
    };

    // --- Initial load ---
    useEffect(() => {
        fetchOffers();
        fetchProducts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Added eslint-disable-line to suppress warning, as we only want this to run once.

    // --- Filter ---
    const handleFilterSubmit = (values) => {
        const filters = {
            offerType: values.offerType || "",
            currentStatus: values.status, // Directly pass "true", "false", or undefined
            startDate: values.dateRange ? dayjs(values.dateRange[0]).format("YYYY-MM-DD") : "",
            endDate: values.dateRange ? dayjs(values.dateRange[1]).format("YYYY-MM-DD") : "",
        };
        fetchOffers(filters, 1, pagination.pageSize);
    };

    // --- Table Pagination ---
    const handleTableChange = (paginationInfo) => {
        const values = filterForm.getFieldsValue();
        const filters = {
            offerType: values.offerType || "",
            // --- 2. Fixed pagination filter bug ---
            // Was incorrectly checking for "active" / "inactive"
            currentStatus: values.status ?? "",
            startDate: values.dateRange ? dayjs(values.dateRange[0]).format("YYYY-MM-DD") : "",
            endDate: values.dateRange ? dayjs(values.dateRange[1]).format("YYYY-MM-DD") : "",
        };
        fetchOffers(filters, paginationInfo.current, paginationInfo.pageSize);
    };

    // --- Modal handlers ---

    const showAddModal = () => {
        setModalType('create');
        setEditingOffer(null);
        modalForm.resetFields();
        modalForm.setFieldsValue({ 
            offerStatus: true,
            offerQty: 0,
            offerMinValue: 0,
            offerMinPoints: 0,
            offerMinPointsTwo: 0,
            offerMinPointsThree: 0
        });
        setIsModalVisible(true);
        if (products.length === 0) {
            fetchProducts();
        }
    };

    const handleEditOffer = (record) => {
        setModalType('edit');
        setEditingOffer(record);
        modalForm.resetFields();
        
        // Populate form
        modalForm.setFieldsValue({
            offerCode: record.offerCode,
            productId: record.offerProductStockId,  // Assuming this is the field name from API get list, need to verify if distinct from create payload
            offerTitle: record.offerTitle,
            offerDesc: record.offerDesc,
            offerDateRange: [
                record.offerStartDate ? dayjs(record.offerStartDate) : null,
                record.offerEndDate ? dayjs(record.offerEndDate) : null
            ],
            offerQty: record.offerQty,
            offerMinValue: record.offerMinValue,
            offerMinPoints: record.offerMinPoints,
            offerMinPointsTwo: record.offerMinPointsTwo || 0,
            offerMinPointsThree: record.offerMinPointsThree || 0,
            offerStatus: String(record.offerStatus).toLowerCase() === 'true' // Handle string vs boolean
        });

        setIsModalVisible(true);
         if (products.length === 0) {
            fetchProducts();
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingOffer(null);
        modalForm.resetFields();
    };

    // --- Create / Edit Offer ---
    const handleFormSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (modalType === 'create') {
                const payload = {
                    offerCode: values.offerCode,
                    offerProductStockId: values.productId,
                    offerTitle: values.offerTitle,
                    offerDesc: values.offerDesc || "",
                    offerStartDate: values.offerDateRange[0].format("YYYY-MM-DD"),
                    offerEndDate: values.offerDateRange[1].format("YYYY-MM-DD"),
                    offerQty: values.offerQty || 0,
                    offerMinValue: values.offerMinValue || 0,
                    offerMinPoints: isM1Applicable ? (values.offerMinPoints || 0) : 0,
                    offerMinPointsTwo: isM2Applicable ? (values.offerMinPointsTwo || 0) : 0,
                    offerMinPointsThree: isM3Applicable ? (values.offerMinPointsThree || 0) : 0,
                    offerStatus: values.offerStatus,
                    createdBy: USERID || "SYSTEM",
                };

                await axios.post(`${ApiURL}/offers/createoffer`, payload, {
                   headers: { SystemAccessToken: TOKEN },
                });
                 message.success("Offer created successfully");

            } else {
                // Edit
                const payload = {
                    offerId: editingOffer.offerId, // Assuming ID is present in record
                    offerTitle: values.offerTitle,
                    offerDesc: values.offerDesc || "",
                    offerStartDate: values.offerDateRange[0].format("YYYY-MM-DD"),
                    offerEndDate: values.offerDateRange[1].format("YYYY-MM-DD"),
                    offerQty: values.offerQty || 0,
                    offerMinValue: values.offerMinValue || 0,
                    offerMinPoints: isM1Applicable ? (values.offerMinPoints || 0) : 0,
                    offerMinPointsTwo: isM2Applicable ? (values.offerMinPointsTwo || 0) : 0,
                    offerMinPointsThree: isM3Applicable ? (values.offerMinPointsThree || 0) : 0,
                    offerStatus: values.offerStatus,
                    updatedBy: USERID || "SYSTEM",
                };

                 await axios.post(`${ApiURL}/offers/updateoffer`, payload, {
                    headers: { SystemAccessToken: TOKEN },
                });
                message.success("Offer updated successfully");
            }
            
            setIsModalVisible(false);
            fetchOffers(); // Refresh table
        } catch (error) {
            console.error("Failed to save offer", error);
            message.error("Failed to save offer");
        } finally {
            setIsLoading(false);
        }
    };

    // --- 1. Refactored Columns ---
    const columns = [
        { title: "Offer Code", dataIndex: "offerCode", key: "offerCode" },

        {
            title: 'Product',
            dataIndex: 'offerProductName',
            key: 'offerProductName',
            width: 300,
            render: (_, record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>[{record.offerProductCode}] - {record.offerProductName}</div>
        },
        {
            title: 'Offer Title',
            dataIndex: 'offerTitle',
            key: 'offerTitle',
            width: 250,
            render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>{text}</div>
        },
        {
            title: 'Start Date',
            dataIndex: 'offerStartDate',
            key: 'offerStartDate',
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'End Date',
            dataIndex: 'offerEndDate',
            key: 'offerEndDate',
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        { title: "Offer QTY", dataIndex: "offerQty", key: "offerQty" },
        { title: "Order Min Value", dataIndex: "offerMinValue", key: "offerMinValue" },
        {
            title: "Offer Min Points",
            key: "offerMinPointsCombined",
            render: (_, record) => {
                const isM1 = ['LMP', 'PTMP', 'WPC1', 'WPC2', 'WPC3', 'WPS1', 'WPS2', 'WPS3'].includes(record.offerCode);
                const isM2 = ['PTMP', 'WPC2', 'WPC3', 'WPS2', 'WPS3'].includes(record.offerCode);
                const isM3 = ['PTMP', 'WPC3', 'WPS3'].includes(record.offerCode);

                if (!isM1) return '-';

                const m1 = record.offerMinPoints ?? 0;
                const m2 = record.offerMinPointsTwo ?? 0;
                const m3 = record.offerMinPointsThree ?? 0;

                if (isM3) {
                    return `M1: ${m1} | M2: ${m2} | M3: ${m3}`;
                }
                if (isM2) {
                    return `M1: ${m1} | M2: ${m2}`;
                }
                return `M1: ${m1}`;
            }
        },
        {
            title: 'Status',
            dataIndex: 'offerStatus', // Changed from 'active'
            key: 'offerStatus',     // Changed from 'active'
            render: (status) => {
                // Handle string "True" or "False" from API
                const isActive = String(status).toLowerCase() === 'true';
                const color = isActive ? 'green' : 'red';
                const text = isActive ? 'Active' : 'Inactive';
                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        variant='outlined'
                        color='yellow'
                        type="primary"
                        size='small'
                        onClick={() => handleEditOffer(record)}
                        disabled={isLoading}
                    >
                        Edit
                    </Button>
                </Space>
            ),
        }
    ];

    return (
        <Spin spinning={isLoading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />
                <Content style={{ padding: '20px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }}>
                        <Breadcrumb.Item><Link to={"/stocks/management"}>Stocks</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Product Offers</Breadcrumb.Item>
                    </Breadcrumb>
                    <Card title="Offer Filter" style={{ marginBottom: 20 }}>
                        {/* --- Filter Form --- */}
                        <Form
                            form={filterForm}
                            layout="inline"
                            onFinish={handleFilterSubmit}
                            style={{ marginBottom: 20 }}
                        >
                            <Form.Item name="offerType" label="Offer Type">
                                <Select
                                    placeholder="Select Offer Type"
                                    style={{ width: 420 }}
                                    popupMatchSelectWidth={false}
                                    allowClear
                                >
                                    {OFFER_TYPES.map((type) => (
                                        <Option key={type.value} value={type.value}>{type.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="status" label="Status">
                                <Select placeholder="Any Status" style={{ width: 150 }} allowClear>
                                    <Option value="true">Active</Option>
                                    <Option value="false">Inactive</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="dateRange" label="Date Range">
                                <RangePicker />
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Filter</Button>
                                    <Button icon={<ClearOutlined />} onClick={() => {
                                        filterForm.resetFields();
                                        fetchOffers(); // Refetch all on clear
                                    }}>Clear</Button>
                                </Space>
                            </Form.Item>
                        </Form>
                        <p>
                            {OFFER_TYPES.map((type, idx) => (
                                <span key={type.value}>
                                    [<small><b>{type.value}</b>={type.label}</small>]{idx < OFFER_TYPES.length - 1 ? ' ' : ''}
                                </span>
                            ))}
                        </p>
                    </Card>

                    <Card
                        title={
                            <Row justify="space-between" align="middle">
                                <Col>Product Offers</Col>
                                <Col>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
                                        Add New Offer
                                    </Button>
                                </Col>
                            </Row>
                        }
                    >

                        {/* --- Offers Table --- */}
                        <Table
                            columns={columns}
                            dataSource={offers}
                            rowKey="offerId" // Use unique ID from API data
                            bordered
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 'max-content' }}
                        />
                    </Card>


                    {/* --- Add Offer Modal --- */}
                    <Modal
                        title={modalType === 'create' ? "New Offer" : "Edit Offer"}
                        open={isModalVisible}
                        onCancel={handleCancel}
                        footer={null}
                        destroyOnClose
                        width={900} // Wider modal
                        centered
                    >
                        <Form
                            form={modalForm}
                            layout="vertical"
                            onFinish={handleFormSubmit}
                            initialValues={{ offerStatus: true }}
                        >
                            <Row gutter={[16, 8]}>
                                {/* Offer Code */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerCode"
                                        label="Offer Type"
                                        rules={[{ required: true, message: "Please select an offer type" }]}
                                    >
                                        <Select
                                            placeholder="Select Offer Type"
                                            allowClear
                                            disabled={modalType === 'edit'}
                                            popupMatchSelectWidth={false}
                                        >
                                            {OFFER_TYPES.map((type) => (
                                                <Option key={type.value} value={type.value}>{type.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                {/* Product */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="productId"
                                        label="Select Product"
                                        rules={[{ required: true, message: "Please select a product" }]}
                                    >
                                        <Select
                                            disabled={modalType === 'edit'}
                                            placeholder="Select Product"
                                            showSearch
                                            allowClear
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {products.map((p) => (
                                                <Option key={p.id} value={p.id}>
                                                    {p.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                {/* Offer Quantity */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerQty"
                                        label="Offer Quantity"
                                        rules={[{ required: true, message: "Please enter offer quantity" }]}
                                    >
                                        <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                {/* Minimum Order Value */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerMinValue"
                                        label="Order Minimum Value"
                                        rules={[{ required: true, message: "Please enter minimum order value" }]}
                                    >
                                        <InputNumber min={0} style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                {/* --- Dynamic Point Fields with Smooth Fade-In Animation --- */}
                                <style>{`
                                    @keyframes offerFieldFadeIn {
                                        from {
                                            opacity: 0;
                                            transform: translateY(-8px);
                                        }
                                        to {
                                            opacity: 1;
                                            transform: translateY(0);
                                        }
                                    }
                                `}</style>

                                {isM1Applicable && (
                                    <Col
                                        xs={24}
                                        md={isM3Applicable ? 8 : 12}
                                        style={{ animation: 'offerFieldFadeIn 0.35s ease-in-out' }}
                                    >
                                        <Form.Item
                                            name="offerMinPoints"
                                            label="Min Points (Month 1)"
                                            rules={[{ required: true, message: "Please enter Month 1 minimum points" }]}
                                        >
                                            <InputNumber min={0} style={{ width: "100%" }} placeholder="Enter Month 1 points" />
                                        </Form.Item>
                                    </Col>
                                )}

                                {isM2Applicable && (
                                    <Col
                                        xs={24}
                                        md={isM3Applicable ? 8 : 12}
                                        style={{ animation: 'offerFieldFadeIn 0.35s ease-in-out' }}
                                    >
                                        <Form.Item
                                            name="offerMinPointsTwo"
                                            label="Min Points (Month 2)"
                                            rules={[{ required: true, message: "Please enter Month 2 minimum points" }]}
                                        >
                                            <InputNumber min={0} style={{ width: "100%" }} placeholder="Enter Month 2 points" />
                                        </Form.Item>
                                    </Col>
                                )}

                                {isM3Applicable && (
                                    <Col
                                        xs={24}
                                        md={8}
                                        style={{ animation: 'offerFieldFadeIn 0.35s ease-in-out' }}
                                    >
                                        <Form.Item
                                            name="offerMinPointsThree"
                                            label="Min Points (Month 3)"
                                            rules={[{ required: true, message: "Please enter Month 3 minimum points" }]}
                                        >
                                            <InputNumber min={0} style={{ width: "100%" }} placeholder="Enter Month 3 points" />
                                        </Form.Item>
                                    </Col>
                                )}

                                {/* Offer Title */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerTitle"
                                        label="Offer Title"
                                        rules={[{ required: true, message: "Please enter a title" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>

                                {/* Offer Date Range */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerDateRange"
                                        label="Offer Duration"
                                        rules={[{ required: true, message: "Please select offer duration" }]}
                                    >
                                        <RangePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                {/* Offer Description */}
                                <Col xs={24}>
                                    <Form.Item
                                        name="offerDesc"
                                        label="Offer Description"
                                        rules={[{ required: true, message: "Please enter offer description" }]}
                                    >
                                        <Input.TextArea rows={3} placeholder="Enter offer details..." />
                                    </Form.Item>
                                </Col>

                                {/* Active Switch */}
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="offerStatus"
                                        label="Active"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row justify="end">
                                <Space>
                                    <Button onClick={handleCancel}>Cancel</Button>
                                    <Button type="primary" htmlType="submit">
                                        {modalType === 'create' ? "Create Offer" : "Update Offer"}
                                    </Button>
                                </Space>
                            </Row>
                        </Form>
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
}

export default ProductOffer;